import { CHECKLIST_STEPS } from '../../data';
import { isDone, displayCents, collectedOnBasis } from '../../utils';
import { tint } from '../../constants';
import s from '../../styles/dashboard.module.css';

// Only the pre-payment steps for now — Upsells/Payment/Posted come with the
// payment feature.
const ACTION_STEPS = CHECKLIST_STEPS.slice(0, 3); // Started, Photos, Finished

// Outlined camera, sized to the surrounding text so it sits on the baseline
// with the photo count.
function CameraIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  );
}

// Framed picture with a horizon and sun, matching the camera's weight and size.
function GalleryIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <path d="M21 15l-5-5L5 21" />
    </svg>
  );
}

// Document with a folded corner — the "log a cost" affordance.
function NoteIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6M9 13h6M9 17h6" />
    </svg>
  );
}

function CheckStep({ label, checked, onToggle }) {
  return (
    <button type="button" className={`${s.checkStep} ${checked ? s.done : ''}`}
            onClick={(e) => { e.stopPropagation(); onToggle(); }}>
      {label}
    </button>
  );
}

// Colour + right-hand wording for each payment state. `payment` is null when
// nothing in the payments table links to this booking.
const PAY_STATE = {
  paid:     { tone: 'payCollected',  label: 'Collected' },
  invoiced: { tone: 'payReceivable', label: 'Invoiced'  },
  hotel:    { tone: 'payReceivable', label: 'Billed to property' },
  comped:   { tone: 'payNoCharge',   label: 'Comped'    },
  ghost:    { tone: 'payUnpaid',     label: 'Unpaid'    },
};

const cash = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

// Booked -> collected, plus tip and any shortfall against the quote.
function PaymentBox({ payment, bookedLabel, taxMode, onCollect }) {
  const state = payment ? (PAY_STATE[payment.status] || PAY_STATE.paid) : null;
  const tone  = state ? s[state.tone] : s.payUnpaid;

  // A payment arrives with tax inside it. Before-tax mode strips that back out,
  // so the two halves of this row are on the same basis and the gap between
  // them is a real gap rather than the tax.
  const rate = payment?.bookTaxRate ?? null;
  const collected = collectedOnBasis(payment?.collectedCents, rate, taxMode);

  // A comp collected nothing by design, so show the intent rather than $0.00.
  const rightAmount = !payment
    ? 'Unpaid'
    : payment.status === 'comped'
      ? 'No charge'
      : cash(collected);

  // The shortfall is a difference between those same two figures, so it has to
  // follow them; left alone it would be the tax-inclusive gap beside pre-tax
  // amounts and would not match the subtraction on screen.
  const varianceCents = collectedOnBasis(payment?.varianceCents, rate, taxMode);
  const varianceLabel = !varianceCents
    ? null
    : `${cash(Math.abs(varianceCents))} ${varianceCents < 0 ? 'below' : 'above'} quote`;

  return (
    <div className={s.bookedBox}>
      <div className={s.payRow}>
        <div className={s.payCol}>
          <div className={s.bookedBoxLabel}>Booked</div>
          <div className={s.bookedBoxAmount}>{bookedLabel}</div>
        </div>
        <span className={s.payArrow}>&rarr;</span>
        <div className={s.payCol}>
          <div className={s.bookedBoxLabel}>{state ? state.label : 'Collected'}</div>
          <div className={`${s.bookedBoxAmount} ${tone}`}>{rightAmount}</div>
        </div>
        <button type="button" className={s.payAction}
                onClick={(e) => { e.stopPropagation(); onCollect(); }}>
          {!payment ? 'Collect' : payment.needsVerification ? 'Verify' : 'Review'}
        </button>
      </div>

      {payment?.tipLabel && (
        <div className={s.payTip}>
          + {payment.tipLabel} tip{payment.cardType ? ' · on the charge' : ''}
        </div>
      )}

      {varianceLabel && (
        <div className={s.payVariance}>
          {varianceLabel}{payment.reason ? ` — ${payment.reason}` : ' — reason unknown'}
        </div>
      )}
    </div>
  );
}

function MiniAvatar({ member, color, onRemove }) {
  const name = member.name || `${member.given_name || ''} ${member.family_name || ''}`.trim();
  const initials = member.initials || name.split(' ').filter(Boolean).map(n => n[0]).join('').toUpperCase();
  return (
    <div style={{
      width: 26, height: 26, borderRadius: '50%', background: tint(color, 13),
      border: `1.5px solid ${tint(color, 40)}`, display: 'flex', alignItems: 'center',
      justifyContent: 'center', fontSize: 9, fontWeight: 600, color,
      cursor: 'pointer', flexShrink: 0,
    }} onClick={(e) => { e.stopPropagation(); onRemove(); }} title={`${name} — click to unassign`}>
      {initials}
    </div>
  );
}

export function AppointmentCard({ appt, date, taxMode, onToggleCheck, accentColor, staff, onAssignStaff, onUnassignStaff, dragOverId, setDragOverId, onUploadClick, onViewPhotos, onNextStep, onCollectPayment, onOpenDetail, onLogCost, uploadStatus }) {
  const done     = isDone(appt);
  const services = appt.services?.length > 0 ? appt.services : [appt.service];
  const cardId   = `${appt.location}-${appt.id}`;
  const isOver   = dragOverId === cardId;
  const assigned = appt.assignedStaff || [];
  // Older payloads only carried the boolean; fall back so the icon still works.
  const photoCount = appt.photoCount ?? (appt.checks[1] ? 1 : 0);

  // "Edward Tsai · booked Aug 19, 04:39 PM by Mike Ford"
  // The booked figure follows the header's before/after-tax switch, using this
  // location's own rate. Only the quote moves: `collected` is real money that
  // already has tax inside it, so re-taxing it would overstate what came in.
  const quotedCents = appt.totalCents != null
    ? appt.totalCents
    : Math.round((parseFloat(String(appt.totalAmount || '').replace(/[^0-9.]/g, '')) || 0) * 100);
  const shownCents = displayCents(quotedCents, appt.taxRate, taxMode);
  const bookedLabel = shownCents == null ? '$0.00' : `$${(shownCents / 100).toFixed(2)}`;

  const bookedLine = [appt.detailer, appt.bookedAtLabel ? `booked ${appt.bookedAtLabel}` : null]
    .filter(Boolean).join(' · ') + (appt.bookedBy ? ` by ${appt.bookedBy}` : '');

  return (
    <div
      className={`${s.apptCard} ${done ? s.done : ''}`}
      style={{
        borderLeftColor: accentColor,
        borderLeftWidth: 3,
        outline: isOver ? `2px solid ${accentColor}` : 'none',
        cursor: onOpenDetail ? 'pointer' : undefined,
      }}
      onClick={() => onOpenDetail?.(appt)}
      onDragOver={e => { e.preventDefault(); setDragOverId(cardId); }}
      onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverId(null); }}
      onDrop={e => {
        e.preventDefault(); setDragOverId(null);
        const memberId = e.dataTransfer.getData('memberId');
        if (memberId) onAssignStaff(appt.location, appt.id, memberId);
      }}
    >
      {/* Time + photo actions */}
      <div className={s.apptHeader}>
        <span className={s.apptTime}>{appt.time}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* Costs paid out on this car: contractor work, supplies. */}
          <button
            onClick={(e) => { e.stopPropagation(); onLogCost?.(appt); }}
            title="Log a cost"
            className={s.noteBtn}
          >
            <NoteIcon />
            {appt.costCount > 0 && <span className={s.cameraCount}>{appt.costCount}</span>}
          </button>

          {appt.payment && (
            <span className={`${s.payBadge} ${s[(PAY_STATE[appt.payment.status] || PAY_STATE.paid).tone]}`}
                  title={appt.payment.methodTitle || ''}>
              {appt.payment.badge}
            </span>
          )}

          {photoCount > 0 && (
            <button
              onClick={(e) => { e.stopPropagation(); onViewPhotos(appt); }}
              title={`View ${photoCount} photo${photoCount === 1 ? '' : 's'}`}
              className={s.galleryBtn}
            >
              <GalleryIcon />
              <span className={s.cameraCount}>{photoCount}</span>
            </button>
          )}

          {/* Upload. The count lives on the view button above, so this stays a
              plain action. */}
          <button
            onClick={(e) => { e.stopPropagation(); onUploadClick(appt); }}
            title="Upload photos"
            disabled={uploadStatus === 'uploading'}
            className={`${s.cameraBtn} ${uploadStatus === 'done' ? s.cameraBtnDone : uploadStatus === 'error' ? s.cameraBtnError : ''}`}
          >
            {uploadStatus === 'uploading' ? '⏳'
              : uploadStatus === 'done'  ? '✓'
              : uploadStatus === 'error' ? '✗'
              : <CameraIcon />}
          </button>
        </div>
      </div>

      {/* Customer name + assigned staff */}
      <div className={s.apptInfo}>
        <div className={`${s.apptCar} ${done ? s.crossed : ''}`}
             style={{ flex: 1, minWidth: 0, fontSize: 16, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {appt.customer}
        </div>
        {assigned.length > 0 && (
          <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
            {assigned.map(memberId => {
              const m = staff.find(x => x.externalId === memberId);
              if (!m) return null;
              return <MiniAvatar key={memberId} member={m} color={accentColor}
                                 onRemove={() => onUnassignStaff(appt.location, appt.id, memberId)} />;
            })}
          </div>
        )}
      </div>

      <div className={s.serviceList}>
        {services.map((svc, i) => (
          <div key={i} className={s.serviceRow} style={{ marginBottom: i < services.length - 1 ? 3 : 0 }}>
            <span className={s.serviceBullet} style={{ color: accentColor }}>▸</span>
            <span className={`${s.serviceText} ${done ? s.serviceTextDone : ''}`}>
              {svc}
            </span>
          </div>
        ))}
      </div>

      {appt.notes && (
        <div className={s.notesBox} style={{ borderLeft: `2px solid ${tint(accentColor, 33)}` }}>
          <div className={s.notesLabel}>Notes</div>
          <div className={s.notesText}>{appt.notes}</div>
        </div>
      )}

      {appt.customerNote && (
        <div className={s.notesBox} style={{ borderLeft: `2px solid ${tint(accentColor, 33)}` }}>
          <div className={s.notesLabel}>Customer Note</div>
          <div className={s.notesText}>{appt.customerNote}</div>
        </div>
      )}

      {bookedLine.trim() && (
        <div className={s.bookedInfo}>{bookedLine}</div>
      )}

      {isOver && (
        <div className={s.dropTarget} style={{ border: `1px dashed ${accentColor}`, color: accentColor }}>
          DROP TO ASSIGN
        </div>
      )}

      <div className={s.checklistRow}>
        {ACTION_STEPS.map((step, i) => {
          // Photos is derived from the uploaded files — the server refuses to
          // set it directly — so this button shows them rather than toggling.
          const isPhotos = step.toLowerCase() === 'photos';
          return (
            <CheckStep
              key={step}
              label={step}
              // Photos reads as done once the car actually has photos, rather
              // than from the checklist flag it is derived from.
              checked={isPhotos ? photoCount > 0 : appt.checks[i]}
              onToggle={() => {
                if (!isPhotos) return onToggleCheck(appt.location, appt.id, i, step);
                // Stays clickable with nothing to show, it just does nothing —
                // a disabled button reads as broken rather than empty.
                if (photoCount > 0) onViewPhotos(appt);
              }}
            />
          );
        })}
      </div>

      <PaymentBox
        payment={appt.payment}
        bookedLabel={bookedLabel}
        taxMode={taxMode}
        onCollect={() => onCollectPayment?.(appt)}
      />
    </div>
  );
}
